import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, Loader2, AlertTriangle, Undo2, AlertOctagon, ChevronLeft } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { UK_PARTS_SUPPLIERS } from '../../mechanic/Returns/constants';
import { errorMessage, reportError } from '../../../lib/errors';

type Track = null | 'supplier' | 'writeoff';

interface ReturnReasonModalProps {
    isOpen: boolean;
    onClose: () => void;
    itemId: string;
    itemDescription: string;
    itemPrice: number;
    itemQuantity: number;
    jobId: string;
    jobCreatedAt: string;
    vehicleVrm: string;
    onSuccess: () => void;
}

export default function ReturnReasonModal({
    isOpen, onClose, itemId, itemDescription, itemPrice, itemQuantity,
    vehicleVrm, onSuccess
}: ReturnReasonModalProps) {
    const [track, setTrack] = useState<Track>(null);
    const [supplier, setSupplier] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [knownSuppliers, setKnownSuppliers] = useState<string[]>([]);

    useEffect(() => {
        if (!isOpen) return;
        // Reset on open
        setTrack(null);
        setSupplier('');
        setNotes('');
        setError('');

        // Fetch suppliers the org has used before so they appear above the presets
        (async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user.id)
                .single();
            if (profileError) reportError(profileError, 'return-reason-modal.profiles');
            if (!profile?.active_org_id) return;
            const { data, error: fetchError } = await supabase
                .from('supplier_profiles')
                .select('name')
                .eq('org_id', profile.active_org_id)
                .order('name');
            if (fetchError) reportError(fetchError, 'return-reason-modal.supplier-profiles');
            if (data) setKnownSuppliers(data.map((s: { name: string }) => s.name));
        })();
    }, [isOpen]);

    const supplierChips = (() => {
        const seen = new Set<string>();
        const combined: string[] = [];
        for (const s of knownSuppliers) {
            const key = s.toLowerCase().trim();
            if (!seen.has(key)) { seen.add(key); combined.push(s); }
        }
        for (const s of UK_PARTS_SUPPLIERS) {
            const key = s.toLowerCase().trim();
            if (!seen.has(key)) { seen.add(key); combined.push(s); }
        }
        return combined;
    })();

    const totalValue = itemQuantity * itemPrice;

    const submitSupplierReturn = async () => {
        if (!supplier.trim()) { setError('Please choose or enter a supplier'); return; }
        setIsSubmitting(true);
        setError('');
        try {
            const { data: { user } } = await supabase.auth.getUser();
            const { data: profile, error: profileError2 } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user?.id)
                .single();
            if (profileError2) reportError(profileError2, 'return-reason-modal.profiles');
            if (!profile?.active_org_id) throw new Error('No org found');

            const { error: rpcError } = await supabase.rpc('fn_flag_part_return', {
                p_org_id: profile.active_org_id,
                p_job_item_id: itemId,
                p_return_reason: 'wrong_part',
                p_return_type: 'supplier',
                p_supplier: supplier.trim(),
                p_notes: notes || null,
                p_flagged_by: user?.id || null,
                p_photo_path: null,
            });
            if (rpcError) throw rpcError;
            onSuccess();
            onClose();
        } catch (err) {
            setError(errorMessage(err) || 'Failed to flag return');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitWriteOff = async () => {
        setIsSubmitting(true);
        setError('');
        try {
            const { data: { user } } = await supabase.auth.getUser();
            const { data: profile, error: profileError3 } = await supabase
                .from('profiles')
                .select('active_org_id')
                .eq('id', user?.id)
                .single();
            if (profileError3) reportError(profileError3, 'return-reason-modal.profiles');
            if (!profile?.active_org_id) throw new Error('No org found');

            const { data: returnId, error: flagError } = await supabase.rpc('fn_flag_part_return', {
                p_org_id: profile.active_org_id,
                p_job_item_id: itemId,
                p_return_reason: 'faulty',
                p_return_type: 'stock',
                p_supplier: 'Damaged — Write-off',
                p_notes: notes || null,
                p_flagged_by: user?.id || null,
                p_photo_path: null,
            });
            if (flagError) throw flagError;

            // Immediately transition to write_off — expense posts to ledger
            const { error: statusError } = await supabase.rpc('fn_update_return_status', {
                p_org_id: profile.active_org_id,
                p_return_id: returnId,
                p_new_status: 'write_off',
            });
            if (statusError) throw statusError;

            onSuccess();
            onClose();
        } catch (err) {
            setError(errorMessage(err) || 'Failed to write off part');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                <div className="absolute inset-0 bg-scrim/60 backdrop-blur-sm" />

                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="relative w-full max-w-md bg-panel border border-tint/10 rounded-2xl shadow-2xl flex flex-col max-h-[90dvh]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between p-4 border-b border-tint/10 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            {track && (
                                <button onClick={() => setTrack(null)} className="p-1 text-fg-4 hover:text-fg">
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                            )}
                            <div className="w-10 h-10 rounded-xl bg-gold-fill/20 flex items-center justify-center">
                                <Undo2 className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-fg">Return Part</h2>
                                <p className="text-xs text-fg-4">{vehicleVrm} • {itemDescription}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 text-fg-4 hover:text-fg hover:bg-tint/10 rounded-lg transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="overflow-y-auto flex-1 min-h-0">
                    <div className="mx-4 mt-4 p-3 rounded-xl bg-gold-fill/5 border border-gold/20">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-sm font-bold text-fg">{itemDescription}</p>
                                <p className="text-xs text-fg-3">Qty: {itemQuantity} × £{itemPrice.toFixed(2)}</p>
                            </div>
                            <p className="text-lg font-mono font-bold text-gold">£{totalValue.toFixed(2)}</p>
                        </div>
                    </div>

                    {/* Track selection */}
                    {track === null && (
                        <div className="p-4 space-y-3">
                            <button
                                onClick={() => setTrack('supplier')}
                                className="w-full flex items-start gap-3 p-4 rounded-xl bg-info/10 border border-info/30 hover:bg-info/15 hover:border-info/50 transition-colors text-left"
                            >
                                <div className="w-10 h-10 rounded-lg bg-info/20 flex items-center justify-center flex-shrink-0">
                                    <RotateCcw className="w-5 h-5 text-info" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-fg">Return to supplier</p>
                                    <p className="text-xs text-fg-3 mt-0.5">Send back with original invoice. Credit will appear on your next statement.</p>
                                </div>
                            </button>

                            <button
                                onClick={() => setTrack('writeoff')}
                                className="w-full flex items-start gap-3 p-4 rounded-xl bg-danger/10 border border-danger/30 hover:bg-danger/15 hover:border-danger/50 transition-colors text-left"
                            >
                                <div className="w-10 h-10 rounded-lg bg-danger/20 flex items-center justify-center flex-shrink-0">
                                    <AlertOctagon className="w-5 h-5 text-danger" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-fg">Write off</p>
                                    <p className="text-xs text-fg-3 mt-0.5">Damaged or unusable. Recorded as a tax-deductible business expense.</p>
                                </div>
                            </button>

                            <p className="text-[11px] text-fg-4 text-center pt-2">
                                The part will be removed from the job. The customer will not be charged for it.
                            </p>
                        </div>
                    )}

                    {/* Supplier track */}
                    {track === 'supplier' && (
                        <div className="p-4 space-y-4">
                            <div>
                                <label className="text-xs font-bold text-fg-4 uppercase mb-2 block">
                                    Supplier *
                                </label>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {supplierChips.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => { setSupplier(s); setError(''); }}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all min-h-[36px] ${supplier === s
                                                ? 'bg-info/20 border-2 border-info/50 text-info'
                                                : 'bg-tint/5 border border-tint/10 text-fg-2 hover:bg-tint/10'
                                                }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                                <input
                                    type="text"
                                    value={supplier}
                                    onChange={(e) => { setSupplier(e.target.value); setError(''); }}
                                    placeholder="Or type another supplier..."
                                    className="w-full bg-tint/5 border border-tint/10 rounded-xl p-3 text-sm text-fg placeholder-fg-4 outline-none focus:ring-2 focus:ring-gold/50 min-h-[48px]"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-fg-4 uppercase mb-2 block">Notes (optional)</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Wrong part, faulty, not needed..."
                                    rows={2}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-xl p-3 text-sm text-fg placeholder-fg-4 outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                                />
                            </div>

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center gap-2 p-3 rounded-xl bg-danger/10 border border-danger/20 text-danger text-sm"
                                >
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                                </motion.div>
                            )}

                            <button
                                onClick={submitSupplierReturn}
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-center gap-2 p-4 rounded-xl bg-info-fill hover:bg-info-fill text-on-fill font-bold text-sm transition-colors disabled:opacity-50 min-h-[48px]"
                            >
                                {isSubmitting ? (
                                    <><Loader2 className="w-5 h-5 animate-spin" /> Flagging...</>
                                ) : (
                                    <><RotateCcw className="w-5 h-5" /> Confirm Return</>
                                )}
                            </button>

                            <p className="text-[11px] text-fg-4 text-center leading-relaxed">
                                Once the supplier credit appears on your statement, upload the statement and we'll match it automatically.
                            </p>
                        </div>
                    )}

                    {/* Write-off track */}
                    {track === 'writeoff' && (
                        <div className="p-4 space-y-4">
                            <div className="p-3 rounded-xl bg-danger/5 border border-danger/20">
                                <p className="text-xs text-fg-3 mb-1">This will post a write-off of</p>
                                <p className="text-2xl font-mono font-bold text-danger">£{totalValue.toFixed(2)}</p>
                                <p className="text-[11px] text-fg-4 mt-2">
                                    Recorded as a tax-deductible business expense. Reduces your taxable profit.
                                </p>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-fg-4 uppercase mb-2 block">Notes (optional)</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="What happened? (e.g. gasket torn during install)"
                                    rows={2}
                                    className="w-full bg-tint/5 border border-tint/10 rounded-xl p-3 text-sm text-fg placeholder-fg-4 outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                                />
                            </div>

                            {error && (
                                <motion.div
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center gap-2 p-3 rounded-xl bg-danger/10 border border-danger/20 text-danger text-sm"
                                >
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
                                </motion.div>
                            )}

                            <button
                                onClick={submitWriteOff}
                                disabled={isSubmitting}
                                className="w-full flex items-center justify-center gap-2 p-4 rounded-xl bg-danger-fill hover:bg-danger-fill text-on-fill font-bold text-sm transition-colors disabled:opacity-50 min-h-[48px]"
                            >
                                {isSubmitting ? (
                                    <><Loader2 className="w-5 h-5 animate-spin" /> Writing off...</>
                                ) : (
                                    <><AlertOctagon className="w-5 h-5" /> Confirm Write-off</>
                                )}
                            </button>
                        </div>
                    )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
